import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadDropzone } from '@duke-hq/svc-files-ui/FileUploadDropzone';
import { FileUploadList } from '@duke-hq/svc-files-ui/FileUploadList';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { FileUploadContext } from '@duke-hq/svc-files-ui/FileUploadContext';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { Drn } from '@duke-hq/drn/type';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

const MAX_SIZE_MB = 25;
const MAX_FILES = 5;

interface UploadProgress {
  isUploading: boolean;
  hasFailedUploads: boolean;
}

const NO_UPLOADS: UploadProgress = { isUploading: false, hasFailedUploads: false };

interface ResolveGuardianConsentModalProps {
  enrolmentId: number;
  studentId: string;
  onClose: () => void;
}

export function ResolveGuardianConsentModal({
  enrolmentId,
  studentId,
  onClose,
}: ResolveGuardianConsentModalProps): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const resolveGuardianConsent = trpcAdmin.enrolment.resolveGuardianConsent.useMutation();
  const [uploadedFileDrns, setUploadedFileDrns] = useState<Drn[]>([]);
  const [uploads, setUploads] = useState<UploadProgress>(NO_UPLOADS);
  const isBusy = uploads.isUploading || resolveGuardianConsent.isPending;

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<{ adminNote: string }>();

  const closeUnlessBusy = () => {
    if (!isBusy) {
      onClose();
    }
  };

  const onSubmit = async ({ adminNote }: { adminNote: string }) => {
    try {
      await resolveGuardianConsent.mutateAsync({
        enrolmentId,
        adminNote,
        fileDrns: uploadedFileDrns,
      });
      toast({ status: 'success', title: t`Guardian consent has been resolved` });
      onClose();
      await utils.invalidate();
    } catch (error) {
      toast({
        status: 'error',
        title: error instanceof Error ? error.message : t`An unknown error occurred`,
      });
    }
  };

  return (
    <Modal isOpen onClose={closeUnlessBusy} size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <Trans>Resolve guardian consent</Trans>
          </ModalHeader>
          <ModalCloseButton isDisabled={isBusy} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>
                  <Trans>Consent form</Trans>
                </FormLabel>
                <FormHelperText mt={0} mb={2}>
                  <Trans>Optional. The form is saved to the student&apos;s documents.</Trans>
                </FormHelperText>
                <FileUploadContextDirect
                  quantity={{ max: MAX_FILES }}
                  accept={ACCEPT}
                  maxSizePerFileMB={MAX_SIZE_MB}
                  attachedToId={studentId}
                  attachedToKind="STUDENT_DOCUMENT"
                  onFileComplete={(output) => {
                    setUploadedFileDrns((drns) => [...drns, output.drn]);
                  }}
                >
                  <ConsentFormUpload onUploadsChange={setUploads} />
                </FileUploadContextDirect>
              </FormControl>
              {uploads.hasFailedUploads ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    <Trans>
                      A file failed to upload. Remove it before resolving, or upload it again.
                    </Trans>
                  </AlertDescription>
                </Alert>
              ) : null}
              <FormControl isInvalid={errors.adminNote != null}>
                <FormLabel>
                  <Trans>Admin note</Trans>
                </FormLabel>
                <Textarea
                  {...register('adminNote', {
                    validate: (value) => value.trim() !== '' || t`Please enter an admin note`,
                  })}
                  placeholder={t`Enter a note about the resolution...`}
                  rows={4}
                />
                <FormErrorMessage>{errors.adminNote?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={resolveGuardianConsent.isPending}
              isDisabled={isBusy || uploads.hasFailedUploads}
            >
              <Trans>Resolve</Trans>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

function ConsentFormUpload({
  onUploadsChange,
}: {
  onUploadsChange: (uploads: UploadProgress) => void;
}): React.ReactElement {
  const fileUploadContext = useContext(FileUploadContext);
  const files = fileUploadContext?.files;
  const isUploading = fileUploadContext?.hasPendingFileUploads ?? false;

  useEffect(() => {
    onUploadsChange({
      isUploading,
      hasFailedUploads: (files ?? []).some((file) => file.failed),
    });
  }, [files, isUploading, onUploadsChange]);

  return (
    <VStack align="stretch" spacing={2}>
      <FileUploadList />
      <FileUploadDropzone />
      <FileUploadTrigger>
        <Button variant="ghost" alignSelf="start">
          <Trans>Choose file</Trans>
        </Button>
      </FileUploadTrigger>
    </VStack>
  );
}
